import * as React from 'react';
import { Fragment, ReactNode } from 'react';
import { action, observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { Modifier, OptionsGeneric } from '@popperjs/core';
import { withTheme } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import DownloadControls from './DownloadControls';
import DocumentMenuTitleControls from './DocumentMenuTitleControls';
import DocumentMenuList from './DocumentMenuList';
import Modal from '../Modal';
import {
    DataStore,
    ConditionalDisclaimerStore,
    ScreenSizeStore,
    SuperscriptScope,
    EntityStore,
} from '../../stores';
import Translate from '../Translate';
import { DisplayOption } from '../../types';
import InlineDisclaimers from '../InlineDisclaimers';
import { Popper } from '../../utils/Popper';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../../models/configuration/explorer/DownloadControls';
import { ColumnDocumentsGrid } from '../../models/configuration/documents/ColumnDocumentsGrid';
import { changeCase } from '@kurtosys/ksys-app-template/dist/utils';

const DropDownWrapper = styled<any, any>(
    'div',
    'dropDownWrapper',
    'documentMenu'
)`
    ${propStyles};
    position: relative;
`;

DropDownWrapper.defaultProps = {
    display: 'inline-flex',
};

interface DocumentMenuTheme {
    DropDownWrapper?: any;
    DocumentMenuList?: any;
    DocumentMenuTitleControls?: any;
    DownloadControls?: any;
}

interface DocumentMenuProps {
    theme: any;
    dataStore?: DataStore;
    screenSizeStore?: ScreenSizeStore;
    entityStore?: EntityStore;
    documents: Array<any>;
    documentGroupLabel?: string;
    showSingleDocument?: boolean;
    showSingleDocumentLabel?: boolean;
    showSingleDocumentGroupLabel?: boolean;
    showMenuTitle?: boolean;
    showCount?: boolean;
    showCheckbox?: boolean;
    documentMenuTheme: DocumentMenuTheme;
    displayOption: DisplayOption;
    metricContext?: string;
    disableExtendedInfo?: boolean;
    superscriptScope?: SuperscriptScope;
    columnDocumentsGrid?: ColumnDocumentsGrid;
    selected?(clientCode: string): boolean;
    onChange?(download: any, key: string): any;
}

@inject('dataStore', 'screenSizeStore', 'entityStore')
@observer
class DocumentMenu extends Popper<DocumentMenuProps> {
    @observable open = false;

    @observable target;
    @observable content;

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'flip',
                enabled: false,
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 5],
                },
            },
        ],
    };

    constructor(props: DocumentMenuProps) {
        super(props);
        this.handleOnChange = this.handleOnChange.bind(this);
    }

    @computed
    get theme() {
        const { theme } = this.props;
        const componentTheme: DocumentMenuTheme = {};
        return { ...theme, ...componentTheme };
    }

    @computed
    get popperEnabled() {
        return this.displayOption === 'dropdown';
    }

    @action
    refTarget = ref => {
        this.target = ref;
        this.setAnchorNodeRef(ref);
    };

    @action
    refContent = ref => {
        this.content = ref;
        this.setPopupNodeRef(ref);
    };

    @action
    toggleDropdown = () => {
        this.open = !this.open;
        this.updatePopper();
    };

    @action
    handleOnChange(document) {
        const { onChange, entityStore } = this.props;
        if (onChange) {
            onChange(document, entityStore?.value.clientCode);
        }
    }

    @computed
    get accessibilityContextForIcon(): string | undefined {
        const { documents } = this.props;
        let docTitle;
        const document = documents && documents.find(doc => doc && doc.title);
        if (document) {
            docTitle = `for ${document.title}`;
        }
        return docTitle;
    }

    @computed
    get showModal() {
        const { screenSizeStore } = this.props;
        return screenSizeStore.compareBreakpoints(
            screenSizeStore.breakpointSize,
            '<=',
            'md'
        );
    }

    @computed
    get displayOption(): DisplayOption {
        const { displayOption = 'dropdown' } = this.props;
        return this.showModal ? 'inline' : displayOption;
    }

    @computed
    get showSingleDocument(): boolean {
        const { documents = [], showSingleDocument = false } = this.props;
        return showSingleDocument && documents.length === 1;
    }

    /**
     * Used to identify document type for disclaimers
     *
     * @readonly
     */
    @computed
    get documentType(): string {
        const { documents, dataStore } = this.props;
        const { documentGroupMeta, documentGroupInferred } = dataStore.config;
        if (documentGroupInferred) {
            return (
                documents.length > 0 &&
                documents[0].inferred &&
                documents[0].inferred[documentGroupInferred]
            );
        } else {
            const documentTypeProperty = documentGroupMeta || 'document_type';
            return (
                documents.length > 0 &&
                documents[0].meta &&
                documents[0].meta[documentTypeProperty] &&
                documents[0].meta[documentTypeProperty].value
            );
        }
    }

    @computed
    get documentGroups(): ReactNode {
        const {
            documents = [],
            children,
            showCount = false,
            documentGroupLabel,
            theme,
            dataStore,
            superscriptScope,
            columnDocumentsGrid,
        } = this.props;

        let conditionalDisclaimers;
        if (dataStore && dataStore.disclaimerStore) {
            conditionalDisclaimers = ConditionalDisclaimerStore.getConditionalsForDocumentTypeAndCulture(
                this.documentType
            );
        }

        const dropDownGroup = (
            <Fragment>
                <DocumentMenuTitleControls
                    documents={documents}
                    onClick={this.toggleDropdown}
                    showCount={showCount}
                    refTarget={this.refTarget}
                    documentGroupLabel={documentGroupLabel}
                    open={this.open}
                    theme={theme}
                    columnId={columnDocumentsGrid?.id}
                >
                    {children}
                </DocumentMenuTitleControls>
                {this.showModal ? this.modalMenu : this.dropDownMenu}
            </Fragment>
        );
        // Wrap drop down component for popup styling
        const component =
            this.displayOption === 'dropdown' ? (
                // role="none" used to prevent accessibility tree from interpretting the div as a generic component
                <DropDownWrapper role="none">{dropDownGroup}</DropDownWrapper>
            ) : (
                dropDownGroup
            );

        return (
            <Fragment>
                {documentGroupLabel && (
                    <InlineDisclaimers
                        conditionalDisclaimers={conditionalDisclaimers}
                        superscriptScope={superscriptScope}
                        analyticsContext={{
                            source: 'documentMenu',
                            sourceContext: documentGroupLabel,
                        }}
                    >
                        <Translate>{documentGroupLabel}</Translate>
                    </InlineDisclaimers>
                )}
                {component}
            </Fragment>
        );
    }

    @computed
    get modalMenu(): ReactNode {
        const { columnDocumentsGrid } = this.props;
        // Due to the documents grid not requiring the document type we can use the colum id
        // Defaulting to empty string for whatever reason the document type and column id is undefined
        const title = this.documentType || columnDocumentsGrid?.id || '';
        return (
            <Modal
                modalId={'fundcenter-modal-document-menu'}
                title={`${changeCase.capitalCase(title)} Documents`}
                headerContent={null}
                bodyContent={null}
                footerContent={null}
                expanderButtonProps={{
                    disable: true,
                }}
                isOpen={this.open}
                onClose={() => {
                    this.open = false;
                }}
            >
                {this.documentMenuList}
            </Modal>
        );
    }

    @computed
    get dropDownMenu(): ReactNode {
        return this.documentMenuList;
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        const { dataStore } = this.props;
        return dataStore?.config.getDownloadControlActionsOrDefault();
    }

    @computed
    get documentMenuList(): ReactNode {
        const {
            documents = [],
            showCheckbox = false,
            onChange,
            selected,
            theme,
            metricContext,
            superscriptScope,
            columnDocumentsGrid,
        } = this.props;

        return (
            <DocumentMenuList
                documents={documents}
                open={this.open}
                showLabel={true}
                showCheckbox={showCheckbox}
                onChange={onChange}
                toggleDropdown={this.toggleDropdown}
                selected={selected}
                refContent={this.refContent}
                displayOption={this.displayOption}
                theme={theme}
                metricContext={`documentMenu-${metricContext}`}
                superscriptScope={superscriptScope}
                documentType={this.documentType}
                columnDocumentsGrid={columnDocumentsGrid}
            />
        );
    }

    @computed
    get document() {
        const {
            documents = [],
            dataStore,
            selected,
            theme,
            showSingleDocumentLabel,
            showSingleDocumentGroupLabel,
            showCheckbox = false,
            documentGroupLabel,
            metricContext,
            entityStore,
            superscriptScope,
            disableExtendedInfo,
        } = this.props;
        if (!dataStore) {
            return null;
        }
        const {
            getDocumentDisplayName,
            downloadDocuments,
            getDocumentLink,
            previewDocuments,
            getFileName,
        } = dataStore;
        const document = documents[0];
        return (
            <DownloadControls
                label={getDocumentDisplayName(document, entityStore?.value)}
                groupLabel={documentGroupLabel}
                showLabel={showSingleDocumentLabel}
                showGroupLabel={showSingleDocumentGroupLabel}
                selected={selected && selected(document.clientCode)}
                onChange={() => this.handleOnChange(document)}
                onClick={e => {
                    e.stopPropagation();
                    downloadDocuments([document], entityStore?.value);
                }}
                href={getDocumentLink([document], entityStore?.value)}
                onPreviewClick={(e, elementOptions: PreviewActionOptions) => {
                    e.stopPropagation();
                    previewDocuments(
                        [document],
                        entityStore?.value,
                        elementOptions
                    );
                }}
                showCount={false}
                count={1}
                theme={{
                    DownloadButton: {
                        flex: '0 0 auto',
                    },
                    ...theme,
                }}
                fileName={getFileName([document], entityStore?.value)}
                showCheckbox={showCheckbox}
                metricContext={`documentMenu-${metricContext}`}
                superscriptScope={superscriptScope}
                documentType={this.documentType}
                document={document}
                actions={this.downloadControlActions}
                disableExtendedInfo={disableExtendedInfo}
            />
        );
    }

    render() {
        return this.showSingleDocument ? this.document : this.documentGroups;
    }
}

export default withTheme(DocumentMenu) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DocumentMenu.tsx